import React, { useState, useEffect } from 'react';
import { ChevronDown, Link as LinkIcon, Loader2, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
import bseLogo from '../assets/images/bselogo.png';
import { publicAPI } from '../lib/api';

// API base URL for download links
// Empty means "same origin" (UAT builds blank and nginx proxies) -- see src/lib/api.js
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:8000';

const CorporateAnnouncement = () => {
  const [openMonth, setOpenMonth] = useState(null);
  const [monthlyReports, setMonthlyReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch monthly summary from API
  useEffect(() => {
    const fetchMonthlySummary = async () => {
      try {
        setLoading(true);
        const response = await publicAPI.getAnnouncementMonthlySummary(12);
        const data = response.data?.data || [];
        
        // API now returns reports array with each month
        const formattedData = data.map(item => ({
          month: item.month_year,
          count: item.count,
          reports: item.reports || []
        }));
        
        setMonthlyReports(formattedData);
      } catch (err) {
        console.error('Failed to fetch monthly summary:', err);
        setError('Failed to load announcement data');
      } finally {
        setLoading(false);
      }
    };

    fetchMonthlySummary();
  }, []);

  const toggleMonth = (index) => {
    setOpenMonth(openMonth === index ? null : index);
  };

  // Get download URL for a report
  const getDownloadUrl = (reportId) => {
    return `${API_BASE_URL}/v1/announcements/ica/${reportId}/public-download/`;
  };

  return (
    <div>
      {/* Banner Section */}
      <section className="bg-[#1b2d4a] py-4">
        <div className="container mx-auto px-6">
          <div className="flex justify-center items-center gap-6">
            <span className="text-white text-lg font-medium">
              To Get Live Important Corporate Announcement
            </span>
            <Link
              to="/pricing"
              className="bg-yellow-400 text-gray-900 px-6 py-2 rounded-full font-semibold hover:bg-yellow-500 transition-colors flex items-center gap-2"
            >
              <LinkIcon size={16} />
              Avail Service
            </Link>
          </div>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h1 className="text-3xl font-bold mb-8 text-gray-900 tracking-wide">
              WHY CORPORATE ANNOUNCEMENTS ARE IMPORTANT?
            </h1>

            <div className="space-y-6 text-gray-700 leading-relaxed mb-12">
              <p>
                The Important Corporate Announcement service is an essential resource for serious investors who track 
                corporate disclosures from listed companies. This service eliminates the manual effort of scanning 
                exchange websites and mitigates the unpredictability associated with secondary sources like social media.
              </p>

              <p>
                We analyse thousands of corporate announcements on the stock exchange (BSE) daily, providing you with 
                a comprehensive report of the most significant disclosures each night. Our service empowers users to 
                navigate special situations, including rights issues, takeovers, buybacks, de-mergers, acquisitions, 
                insolvency resolutions, and various arbitrage opportunities.
              </p>

              <p>
                If you're looking to enhance your investment strategy by interpreting corporate filings, our service 
                is the ideal solution for you.
              </p>
            </div>

            {/* Monthly Reports Accordion */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-[#1b2d4a]" />
                </div>
              ) : error ? (
                <div className="text-center py-12 text-red-500">{error}</div>
              ) : monthlyReports.length === 0 ? (
                <div className="text-center py-12 text-gray-500">No announcements available</div>
              ) : (
                monthlyReports.map((item, index) => (
                  <div key={index} className="border-b border-gray-200 last:border-b-0">
                    <button
                      onClick={() => toggleMonth(index)}
                      className={`w-full flex justify-between items-center px-6 py-4 hover:bg-gray-50 transition-colors ${openMonth === index ? 'bg-blue-50' : ''}`}
                    >
                      <span className="font-bold text-gray-900">
                        {item.month} ({item.count} reports)
                      </span>
                      <ChevronDown 
                        size={20} 
                        className={`text-gray-500 transition-transform ${openMonth === index ? 'rotate-180' : ''}`} 
                      />
                    </button>
                    {openMonth === index && (
                      <div className="bg-white border-t border-gray-200">
                        {item.reports.length > 0 ? (
                          <div className="p-4">
                            <table className="w-full">
                              <thead>
                                <tr className="border-b">
                                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Date</th>
                                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600">Report</th>
                                </tr>
                              </thead>
                              <tbody>
                                {item.reports.map((report, rIndex) => (
                                  <tr key={rIndex} className="border-b last:border-b-0">
                                    <td className="py-3 px-4 text-gray-700">{report.date}</td>
                                    <td className="py-3 px-4 text-right">
                                      {report.has_file ? (
                                        <a
                                          href={getDownloadUrl(report.id)}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-blue-700 transition-colors"
                                        >
                                          <Download size={16} />
                                          Download
                                        </a>
                                      ) : (
                                        <span className="text-gray-400 text-sm">No file available</span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p className="text-gray-500 text-center py-4">
                            No reports available for this month.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </section>

      {/* What Kind of Events Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-bold mb-10 text-[#1b2d4a]">
              WHAT KIND OF EVENTS CAN YOU TRACK WITH IMPORTANT CORPORATE ANNOUNCEMENT
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-6">
              <div className="text-sm font-bold text-gray-500 uppercase tracking-wide">KEYPOINTERS</div>
              <div className="text-sm font-bold text-gray-500 uppercase tracking-wide">EVENTS TRACKED</div>
              
              <div className="text-gray-700">Investor presentation</div>
              <div className="text-gray-600">Track any presentation released during the day</div>
              
              <div className="text-gray-700">Amalgamation/ Demerger</div>
              <div className="text-gray-600">Special situation</div>
              
              <div className="text-gray-700">Buyback</div>
              <div className="text-gray-600">Track buyback announcements and updates</div>
              
              <div className="text-gray-700">Strike</div>
              <div className="text-gray-600">Track labour unrest or shut down situations</div>
              
              <div className="text-gray-700">Expansion</div>
              <div className="text-gray-600">Track capacity expansion announcement</div>
              
              <div className="text-gray-700">Right issue</div>
              <div className="text-gray-600">Special situation</div>
              
              <div className="text-gray-700">Forensic auditor</div>
              <div className="text-gray-600">Track appointment of forensic auditor by SEBI; Fraud warning</div>
              
              <div className="text-gray-700">Object clause</div>
              <div className="text-gray-600">Track changes in business line, diversification, etc.</div>
            </div>

            {/* Source */}
            <div className="mt-12 flex items-center gap-8">
              <span className="text-sm font-bold text-gray-500 uppercase">SOURCE:</span>
              <img src="https://www.nseindia.com/assets/images/NSE_Logo.svg" alt="NSE" className="h-8" />
              <img src={bseLogo} alt="BSE" className="h-8" />
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-xl font-bold mb-10 text-[#1b2d4a]">
              CLIMB CAPITAL – Important Corporate Announcements V/S Other Sources – Manual
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              {/* Left Column - Manual Process Issues */}
              <div className="space-y-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Important corporate announcement</h3>
                
                <p className="text-gray-600 italic">
                  With 3,000-5,000 announcements on the exchange each day, consistent manual review is impractical.
                </p>
                
                <p className="text-gray-600 italic">
                  You may spend 2-3 hours daily sifting through exchange disclosures for new ideas, which could be better utilized for analysis.
                </p>
                
                <p className="text-gray-600 italic">
                  The exchange website can be difficult to navigate, making it hard to distinguish standard disclosures from valuable insights.
                </p>
                
                <p className="text-gray-600 italic">
                  You have no control over which types of corporate events to track, requiring you to review all announcements to find what interests you, such as special situations.
                </p>
              </div>

              {/* Right Column - Our Solution */}
              <div className="space-y-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Manual Process</h3>
                
                <p className="text-gray-600">
                  Our users receive carefully curated reports featuring only 20-30 high-quality announcements daily, ensuring you stay informed about the most relevant developments, allowing you to focus on key insights.
                </p>
                
                <p className="text-gray-600">
                  We save you over 500 man-hours annually, freeing up your time to focus on analysis and strategy rather than sifting through disclosures.
                </p>
                
                <p className="text-gray-600">
                  Our user-friendly platform simplifies the process, making it easy to identify and access valuable announcements without the clutter of standard disclosures.
                </p>
                
                <p className="text-gray-600">
                  We filter announcements based on frequently used keywords, giving you control over the specific types of corporate events you wish to track, such as special situations.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CorporateAnnouncement;
